(() => {
  if (window.RareTCGHeader) return;

  const mobileSearchViewport = window.matchMedia('(max-width: 749px)');
  let latestKnownCount = 0;

  function getCartLink() {
    return document.getElementById('cart-icon-bubble');
  }

  function ensureCartIcon(link) {
    if (!link || link.querySelector('.raretcg-cart-icon')) return;
    const fallbackIcon = link.dataset.rtcgCartIconFallback;
    if (!fallbackIcon) return;

    link.querySelectorAll('svg, .svg-wrapper').forEach((node) => node.remove());
    const image = document.createElement('img');
    image.className = 'raretcg-header-custom-icon raretcg-cart-icon';
    image.src = fallbackIcon;
    image.alt = '';
    image.width = 60;
    image.height = 60;
    image.loading = 'eager';
    link.querySelector('.rtcg-cart-icon-slot')?.prepend(image);
  }

  function setCartBubble(count, options = {}) {
    const link = getCartLink();
    if (!link) return;

    const safeCount = Math.max(0, Number(count) || 0);
    latestKnownCount = safeCount;
    link.dataset.rtcgCartCount = String(safeCount);
    ensureCartIcon(link);

    const slot = link.querySelector('.rtcg-cart-icon-slot') || link;

    // RareTCG owns the header badge. Remove any legacy Dawn bubble that may
    // have been injected by cached section HTML before creating/updating ours.
    link.querySelectorAll('.cart-count-bubble').forEach((legacyBubble) => legacyBubble.remove());

    let bubble = slot.querySelector('[data-rtcg-cart-count-bubble]');

    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'rtcg-header-count-badge rtcg-header-cart__count';
      bubble.dataset.rtcgCartCountBubble = '';
      bubble.innerHTML = '<span aria-hidden="true" data-rtcg-cart-count-text></span><span class="visually-hidden" data-rtcg-cart-count-label></span>';
      slot.appendChild(bubble);
    }

    const countNode = bubble.querySelector('[data-rtcg-cart-count-text]');
    const labelNode = bubble.querySelector('[data-rtcg-cart-count-label]');
    const formattedCount = safeCount > 99 ? '99+' : String(safeCount);

    if (countNode) countNode.textContent = formattedCount;
    if (labelNode) labelNode.textContent = `${safeCount} item${safeCount === 1 ? '' : 's'} in cart`;
    bubble.toggleAttribute('hidden', safeCount === 0);
    bubble.classList.toggle('is-hidden', safeCount === 0);

    if (safeCount > 0 && options.animate !== false) {
      bubble.classList.remove('is-updating');
      void bubble.offsetWidth;
      bubble.classList.add('is-updating');
    }
  }

  function initializeCartBubble() {
    const link = getCartLink();
    if (!link) return;
    const currentState = window.RareTCGCartState?.get();
    const initialCount = currentState?.item_count ?? Number(link.dataset.rtcgCartCount || latestKnownCount);
    setCartBubble(initialCount, { animate: false });
  }


  // -------------------------------------------------------------------
  // Wishlist header integration
  // -------------------------------------------------------------------
  // Wishlist Hero is asynchronous. RareTCG therefore owns all visible header
  // chrome and treats the app block as a hidden bridge only. The SDK is the
  // authoritative count source when available; the bridge DOM is a fallback
  // for stores/plans where the SDK is not exposed.
  const wishlistNativeCountSelector = [
    '.wishlist-hero-items-count',
    '.wishlist-hero-header-count',
    '.MuiBadge-badge',
    '[class*="wishlist"][class*="count" i]',
    '[class*="wlh"][class*="count" i]',
    '[class*="badge" i]'
  ].join(',');

  let wishlistCount = null;
  let wishlistObserver = null;
  let wishlistRefreshSequence = 0;

  function normalizeWishlistBridges() {
    document.querySelectorAll('[data-rtcg-wishlist-app-bridge]').forEach((bridge) => {
      bridge.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((control) => {
        control.setAttribute('tabindex', '-1');
      });
    });
  }

  function setWishlistBadges(count, options = {}) {
    const safeCount = Math.max(0, Number(count) || 0);
    const formatted = safeCount > 99 ? '99+' : String(safeCount);
    const changed = wishlistCount !== safeCount;
    wishlistCount = safeCount;

    document.querySelectorAll('[data-rtcg-wishlist-count]').forEach((badge) => {
      badge.textContent = formatted;
      badge.toggleAttribute('hidden', safeCount === 0);
      badge.classList.toggle('is-hidden', safeCount === 0);

      if (safeCount > 0 && changed && options.animate !== false) {
        badge.classList.remove('is-updating');
        void badge.offsetWidth;
        badge.classList.add('is-updating');
      }
    });
  }

  function parseWishlistCount(node) {
    if (!node) return null;
    const candidates = [
      node.dataset?.count,
      node.dataset?.wishlistCount,
      node.getAttribute?.('aria-label'),
      node.getAttribute?.('title'),
      node.textContent
    ];

    for (const value of candidates) {
      const match = String(value ?? '').match(/\d+/);
      if (match) return Math.max(0, Number(match[0]) || 0);
    }
    return null;
  }

  function readWishlistCountFromBridge() {
    normalizeWishlistBridges();
    const bridges = document.querySelectorAll('[data-rtcg-wishlist-app-bridge]');

    for (const bridge of bridges) {
      const sources = bridge.querySelectorAll(wishlistNativeCountSelector);
      for (const source of sources) {
        const count = parseWishlistCount(source);
        if (count !== null) return count;
      }
    }
    return null;
  }

  async function readWishlistCountFromSdk() {
    const sdk = window.WishListHero_SDK;
    if (!sdk || typeof sdk.GetWishListItemsCount !== 'function') return null;

    try {
      const result = await Promise.resolve(sdk.GetWishListItemsCount());
      const count = Number(result);
      return Number.isFinite(count) ? Math.max(0, count) : null;
    } catch (_error) {
      return null;
    }
  }

  async function refreshWishlistCount(options = {}) {
    const sdkCount = await readWishlistCountFromSdk();
    if (sdkCount !== null) {
      setWishlistBadges(sdkCount, options);
      return sdkCount;
    }

    const bridgeCount = readWishlistCountFromBridge();
    if (bridgeCount !== null) {
      setWishlistBadges(bridgeCount, options);
      return bridgeCount;
    }

    // Missing DOM count during app hydration is not proof of an empty wishlist.
    // Only a user-initiated refresh sequence may resolve a missing fallback to 0.
    if (options.allowMissingAsZero === true && wishlistCount !== null) {
      setWishlistBadges(0, options);
      return 0;
    }
    return wishlistCount;
  }

  function scheduleWishlistRefresh(options = {}) {
    const sequence = ++wishlistRefreshSequence;
    const delays = [60, 320, 850];

    delays.forEach((delay, index) => {
      window.setTimeout(() => {
        if (sequence !== wishlistRefreshSequence) return;
        refreshWishlistCount({ animate: index > 0 && options.animate !== false });
      }, delay);
    });

    if (options.allowMissingAsZero === true) {
      window.setTimeout(() => {
        if (sequence !== wishlistRefreshSequence) return;
        refreshWishlistCount({ animate: true, allowMissingAsZero: true });
      }, 1350);
    }
  }

  function mutationTouchesWishlistBridge(mutation) {
    const target = mutation.target?.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target?.parentElement;
    if (target?.closest?.('[data-rtcg-wishlist-app-bridge]')) return true;

    return [...(mutation.addedNodes || [])].some((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      return node.matches?.('[data-rtcg-wishlist-app-bridge]')
        || node.querySelector?.('[data-rtcg-wishlist-app-bridge]');
    });
  }

  function initializeWishlistIntegration() {
    normalizeWishlistBridges();
    refreshWishlistCount({ animate: false });

    if (wishlistObserver || !document.body) return;
    let queued = false;
    wishlistObserver = new MutationObserver((mutations) => {
      if (!mutations.some(mutationTouchesWishlistBridge) || queued) return;
      queued = true;
      window.setTimeout(() => {
        queued = false;
        refreshWishlistCount({ animate: true });
      }, 120);
    });
    wishlistObserver.observe(document.body, { subtree: true, childList: true, characterData: true });
  }

  function getWishlistBridgeControl() {
    return document.querySelector(
      '[data-rtcg-wishlist-app-bridge] .wishlist-hero-header-icon, '
      + '[data-rtcg-wishlist-app-bridge] a[href="#hero-wishlist"], '
      + '[data-rtcg-wishlist-app-bridge] a, '
      + '[data-rtcg-wishlist-app-bridge] button'
    );
  }

  function openWishlist(event) {
    const trigger = event.target.closest?.('[data-rtcg-wishlist-open]');
    if (!trigger) return;
    event.preventDefault();

    const sdk = window.WishListHero_SDK;
    if (sdk && typeof sdk.OpenWishList === 'function') {
      try {
        Promise.resolve(sdk.OpenWishList()).catch(() => {});
        return;
      } catch (_error) {
        // Fall through to the app bridge.
      }
    }

    const bridgeControl = getWishlistBridgeControl();
    if (bridgeControl) {
      bridgeControl.click();
      return;
    }

    // Last-resort compatibility with Wishlist Hero's standard header contract.
    if (window.location.hash !== '#hero-wishlist') window.location.hash = 'hero-wishlist';
  }

  function handleWishlistAppInteraction(event) {
    if (event.target.closest?.('[data-rtcg-wishlist-open]')) return;
    const appInteraction = event.target.closest?.(
      '.wishlist-hero-custom-button, '
      + '.wishlist-hero-list-card button, '
      + '.wishlist-hero-list-card a, '
      + '[class*="wishlist-hero"] button, '
      + '[class*="wishlist-hero"] a'
    );
    if (!appInteraction) return;
    scheduleWishlistRefresh({ allowMissingAsZero: true });
  }

  document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => {
    refreshWishlistCount({ animate: false });
  });

  function ensureDrawerVisible(scope = document) {
    const header = scope.matches?.('.section-header') ? scope.querySelector('.header') : scope.querySelector?.('.section-header .header');
    const drawer = header?.querySelector('header-drawer');
    if (drawer) drawer.dataset.rtcgDrawerReady = 'true';
  }

  function syncSearchLayer(predictiveSearch) {
    requestAnimationFrame(() => predictiveSearch?.syncBodyState?.());
  }

  function setupSearchForm(form) {
    if (form.dataset.rtcgSearchGuardReady === 'true') return;

    const input = form.querySelector('input[type="search"], [data-rtcg-search-input]');
    const button = form.querySelector('button[type="submit"], [data-rtcg-search-submit]');
    const resetButton = form.querySelector('button[type="reset"], .raretcg-search-reset');
    const predictiveSearch = form.closest('predictive-search');
    const discoveryPanel = form.querySelector('[data-rtcg-search-discovery]');
    if (!input || !button) return;

    form.dataset.rtcgSearchGuardReady = 'true';
    input.removeAttribute('pattern');
    input.removeAttribute('required');
    form.noValidate = true;

    const setDiscoveryVisibility = (shouldShow) => {
      if (!discoveryPanel) return;
      discoveryPanel.toggleAttribute('hidden', !shouldShow || !!(predictiveSearch || form).rtcgMobileSuspended);
      syncSearchLayer(predictiveSearch);
    };

    const syncSearchState = () => {
      if ((predictiveSearch || form).rtcgMobileRestoringFocus) { setDiscoveryVisibility(false); return; }
      const hasValue = input.value.trim().length > 0;
      button.disabled = !hasValue;
      button.classList.toggle('is-disabled', !hasValue);
      form.classList.toggle('has-search-value', hasValue);
      predictiveSearch?.classList.toggle('has-search-value', hasValue);
      resetButton?.toggleAttribute('hidden', !hasValue);

      const focusInsideDiscovery = discoveryPanel?.contains(document.activeElement);
      const mobileOwned = (predictiveSearch || form).dataset.rtcgMobileOwned === 'true';
      setDiscoveryVisibility((mobileOwned || document.activeElement === input || focusInsideDiscovery) && !hasValue);
    };

    const navigateDiscoveryLink = (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !discoveryPanel?.contains(link)) return;
      if (event.type === 'pointerdown' && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      const mobileNavigate = (predictiveSearch || form).rtcgMobileNavigate;
      if (mobileNavigate) mobileNavigate(link.href);
      else window.location.assign(link.href);
    };

    syncSearchState();
    input.addEventListener('input', syncSearchState);
    input.addEventListener('change', syncSearchState);
    input.addEventListener('focus', () => {
      if (predictiveSearch) delete predictiveSearch.dataset.rtcgPreserveMobilePanel;
      syncSearchState();
    });
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        const preservePanel = (predictiveSearch || form).dataset.rtcgMobileOwned === 'true' || predictiveSearch?.dataset.rtcgPreserveMobilePanel === 'true';
        const focusInsideSearch = predictiveSearch?.contains(document.activeElement);
        if (preservePanel) {
          if (!input.value.trim()) setDiscoveryVisibility(true);
          else syncSearchLayer(predictiveSearch);
        } else if (focusInsideSearch) {
          syncSearchState();
        } else {
          setDiscoveryVisibility(false);
        }
      }, 0);
    });

    discoveryPanel?.addEventListener('pointerdown', navigateDiscoveryLink);
    discoveryPanel?.addEventListener('click', navigateDiscoveryLink);
    form.addEventListener('reset', () => {
      requestAnimationFrame(() => {
        // The owner may have closed and restored this node since reset.
        if ((predictiveSearch || form).rtcgMobileSuspended) return;
        syncSearchState();
        input.focus();
      });
      window.setTimeout(syncSearchState, 0);
    });
    form.addEventListener('submit', (event) => {
      if (input.value.trim()) return;
      event.preventDefault();
      syncSearchState();
    });
  }

  function setupSearchForms(scope = document) {
    const forms = scope.matches?.('form') ? [scope] : [...(scope.querySelectorAll?.('form[action]') || [])];
    forms.filter((form) => {
      try {
        return new URL(form.action, window.location.origin).pathname.endsWith('/search');
      } catch (_error) {
        return false;
      }
    }).forEach(setupSearchForm);
  }

  function blurActiveMobileSearchInput() {
    if (!mobileSearchViewport.matches) return false;
    const activeElement = document.activeElement;
    if (!activeElement?.matches?.('.header [data-rtcg-search-input]')) return false;

    const predictiveSearch = activeElement.closest('predictive-search.raretcg-header-search');
    const focusParkingTarget = predictiveSearch?.querySelector('[data-predictive-search]');
    if (predictiveSearch) predictiveSearch.dataset.rtcgPreserveMobilePanel = 'true';
    if (focusParkingTarget) focusParkingTarget.focus({ preventScroll: true });
    else activeElement.blur();
    syncSearchLayer(predictiveSearch);
    return true;
  }

  function closeSearchPanelsOutside(event) {
    const activePredictiveSearch = document.activeElement?.closest?.('predictive-search.raretcg-header-search');
    const alreadyPreservingPanel = activePredictiveSearch?.dataset.rtcgPreserveMobilePanel === 'true';

    if (
      !event.target.closest?.('[data-rtcg-search-input]')
      && !alreadyPreservingPanel
      && blurActiveMobileSearchInput()
    ) return;

    document.querySelectorAll('.header predictive-search.raretcg-header-search').forEach((predictiveSearch) => {
      if (predictiveSearch.contains(event.target)) return;
      delete predictiveSearch.dataset.rtcgPreserveMobilePanel;
      const discoveryPanel = predictiveSearch.querySelector('[data-rtcg-search-discovery]');
      if (discoveryPanel) discoveryPanel.hidden = true;
      predictiveSearch.close?.();
    });

    syncSearchLayer(document.querySelector('.header predictive-search'));
  }

  function initialize(scope = document) {
    initializeCartBubble();
    initializeWishlistIntegration();
    ensureDrawerVisible(scope);
    setupSearchForms(scope);
  }

  window.RareTCGCartState?.subscribe((state) => setCartBubble(state.item_count));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('pointerdown', closeSearchPanelsOutside, true);
  document.addEventListener('touchmove', blurActiveMobileSearchInput, { passive: true });
  document.addEventListener('click', openWishlist);
  document.addEventListener('click', handleWishlistAppInteraction, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  window.RareTCGHeader = { initialize, setCartBubble, refreshWishlistCount, setWishlistBadges };
  window.RareTCGCartBubble = { set: setCartBubble, setBubble: setCartBubble };
})();
